import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useRegisterGlobalSearch, useUser } from 'cor-business'
import type { GlobalSearchAdapter, GlobalSearchResult } from 'cor-business'
import ArchiveIcon from '../../assets/icon/ArchiveIcon'
import CopyLinkIcon from '../../assets/icon/CopyLinkIcon'
import DocumentDuplicateIcon from '../../assets/icon/DocumentDuplicateIcon'
import DuplicateIcon from '../../assets/icon/DuplicateIcon'
import EditIcon from '../../assets/icon/EditIcon'
import MoveIcon from '../../assets/icon/MoveIcon'
import ProjectDuplicateIcon from '../../assets/icon/ProjectDuplicateIcon'
import ProjectStatusesIcon from '../../assets/icon/ProjectStatusesIcon'
import PrivacySettingsIcon from '../../assets/icon/PrivacySettingsIcon'
import TaskMoveIcon from '../../assets/icon/TaskMoveIcon'
import TrashIcon from '../../assets/icon/TrashIcon'
import ProjectDeleteModal from '../ProjectControls/ProjectDeleteModal'
import ProjectDuplicateModal from '../ProjectControls/ProjectDuplicateModal'
import ProjectRenameModal from '../ProjectControls/ProjectRenameModal'
import ProjectStatusModal from '../ProjectControls/ProjectStatusModal'
import TaskMoveModal from '../ProjectControls/TaskMoveModal'
import ShareProjectModal from '../ShareProjectModal'
import { useWorkspace } from '../../context/workspace.context'
import DocumentService from '../../services/document.service'
import ProjectService from '../../services/project.service'
import SearchService from '../../services/search.service'
import TaskService from '../../services/task.service'
import type { Project } from '../../types'
import type { Task } from '../../types/task'
import { buildTaskUpdatePayload } from '../../page/Project/utils/taskPayload'
import { moveTaskToProject } from '../../page/Project/utils/taskMove'
import type { ProjectTaskStatusItem } from '../../utils/projectStatus'
import { copyProjectLink, copyProjectTaskLink } from '../../utils/projectLink'
import { buildProjectDocumentLink, openProjectDocument } from '../../utils/projectDocument'
import {
    navigateToSearchResult,
    SearchResultNavigationError,
} from '../../utils/searchResultNavigation'
import styles from './index.module.scss'

type ProjectActionId = 'rename' | 'duplicate' | 'copy-link' | 'privacy' | 'statuses' | 'archive' | 'delete'
type TaskActionId = 'rename' | 'duplicate' | 'copy-link' | 'move' | 'archive' | 'delete'
type DocumentActionId = 'rename' | 'duplicate' | 'copy-link' | 'archive' | 'move' | 'delete'
type SearchTaskContext = {
    task: Task & Record<string, any>
    project: Project
}
type SearchDocumentContext = {
    result: GlobalSearchResult
    projectId: string
}

function getDocumentBaseName(name: string) {
    const lastDotIndex = name.lastIndexOf('.')
    return lastDotIndex > 0 ? name.slice(0, lastDotIndex) : name
}

export default function TaskGlobalSearchBridge() {
    const navigate = useNavigate()
    const { user } = useUser()
    const { workspace, workspaces, setWorkspace, reloadWorkspaces } = useWorkspace()
    const currentUserIds = useMemo(() => [
        user?.user_id,
        user?.id,
        user?.profile?.user_id,
        user?.profile?.id,
    ].filter((value) => value != null), [user])
    const currentUserEmails = useMemo(() => [
        user?.email,
        user?.profile?.email,
    ].filter((value): value is string => Boolean(value)), [user])
    const [renameProject, setRenameProject] = useState<Project | null>(null)
    const [duplicateProject, setDuplicateProject] = useState<Project | null>(null)
    const [privacyProject, setPrivacyProject] = useState<Project | null>(null)
    const [statusProject, setStatusProject] = useState<Project | null>(null)
    const [statusModalStatuses, setStatusModalStatuses] = useState<ProjectTaskStatusItem[]>([])
    const [deleteProject, setDeleteProject] = useState<Project | null>(null)
    const [taskToRename, setTaskToRename] = useState<SearchTaskContext | null>(null)
    const [taskToMove, setTaskToMove] = useState<SearchTaskContext | null>(null)
    const [taskToDelete, setTaskToDelete] = useState<SearchTaskContext | null>(null)
    const [documentToRename, setDocumentToRename] = useState<SearchDocumentContext | null>(null)
    const [documentToMove, setDocumentToMove] = useState<SearchDocumentContext | null>(null)
    const [documentToDelete, setDocumentToDelete] = useState<SearchDocumentContext | null>(null)
    const [navigationError, setNavigationError] = useState<string | null>(null)

    useEffect(() => {
        if (!navigationError) return

        const timeoutId = window.setTimeout(() => setNavigationError(null), 6000)
        return () => window.clearTimeout(timeoutId)
    }, [navigationError])

    const loadProject = useCallback(async (result: GlobalSearchResult): Promise<Project> => {
        try {
            const response = await ProjectService.get(String(result.id))
            return (response?.project || response) as Project
        } catch {
            return {
                id: Number(result.id),
                name: result.title,
                color: result.color || '#B1AEC7',
                created_at: result.date || '',
                created_by_id: '',
                description: '',
                is_public: true,
                updated_at: '',
                workspace_id: Number(workspace?.id || 0),
                task_statuses: [],
            }
        }
    }, [workspace?.id])

    const handleProjectAction = useCallback(async (actionId: ProjectActionId, result: GlobalSearchResult) => {
        try {
            if (actionId === 'copy-link') {
                await copyProjectLink(result.id)
                return
            }

            const project = await loadProject(result)

            if (actionId === 'rename') {
                setRenameProject(project)
                return
            }

            if (actionId === 'duplicate') {
                setDuplicateProject(project)
                return
            }

            if (actionId === 'privacy') {
                setPrivacyProject(project)
                return
            }

            if (actionId === 'statuses') {
                const statuses = await ProjectService.getSortedTaskStatuses(String(project.id))
                setStatusModalStatuses(statuses)
                setStatusProject(project)
                return
            }

            if (actionId === 'archive') {
                await ProjectService.archive(project.id)
                return
            }

            setDeleteProject(project)
        } catch (error) {
            console.error(`Failed to run project search action "${actionId}":`, error)
        }
    }, [loadProject])

    const loadTaskContext = useCallback(async (result: GlobalSearchResult): Promise<SearchTaskContext> => {
        const taskResponse = await TaskService.get(result.id)
        const task = (taskResponse?.task || taskResponse) as Task & Record<string, any>
        const projectId = task.project_id ?? result.taskDetails?.project?.id

        if (projectId == null) {
            throw new Error(`Project is missing for task ${result.id}`)
        }

        const projectResponse = await ProjectService.get(String(projectId))
        const project = (projectResponse?.project || projectResponse) as Project

        return { task, project }
    }, [])

    const handleTaskAction = useCallback(async (actionId: TaskActionId, result: GlobalSearchResult) => {
        try {
            const projectId = result.taskDetails?.project?.id

            if (actionId === 'copy-link' && projectId != null) {
                await copyProjectTaskLink(projectId, result.id, 'list')
                return
            }

            const context = await loadTaskContext(result)

            if (actionId === 'copy-link') {
                await copyProjectTaskLink(context.project.id, context.task.id, 'list')
                return
            }

            if (actionId === 'rename') {
                setTaskToRename(context)
                return
            }

            if (actionId === 'duplicate') {
                await TaskService.create(buildTaskUpdatePayload(context.task, Number(context.project.id), {
                    title: `${context.task.title || 'Задача'} (копия)`,
                }))
                return
            }

            if (actionId === 'move') {
                setTaskToMove(context)
                return
            }

            if (actionId === 'archive') {
                await TaskService.archive(Number(context.task.id))
                return
            }

            setTaskToDelete(context)
        } catch (error) {
            console.error(`Failed to run task search action "${actionId}":`, error)
        }
    }, [loadTaskContext])

    const handleDocumentAction = useCallback(async (actionId: DocumentActionId, result: GlobalSearchResult) => {
        try {
            const projectIdValue = result.documentDetails?.projectId
            if (projectIdValue == null) {
                throw new Error(`Project is missing for document ${result.id}`)
            }

            const context = { result, projectId: String(projectIdValue) }

            if (actionId === 'rename') {
                setDocumentToRename(context)
                return
            }

            if (actionId === 'duplicate') {
                await DocumentService.duplicate(context.projectId, result.id)
                return
            }

            if (actionId === 'copy-link') {
                await navigator.clipboard.writeText(buildProjectDocumentLink(context.projectId, result.id))
                return
            }

            if (actionId === 'archive') {
                if (result.documentDetails?.isArchived) {
                    await DocumentService.unarchive(context.projectId, result.id)
                } else {
                    await DocumentService.archive(context.projectId, result.id)
                }
                return
            }

            if (actionId === 'move') {
                setDocumentToMove(context)
                return
            }

            setDocumentToDelete(context)
        } catch (error) {
            console.error(`Failed to run document search action "${actionId}":`, error)
        }
    }, [])

    const openSearchResult = useCallback(async (result: GlobalSearchResult) => {
        if (result.type === 'user' && result.href?.startsWith('/participants/user/')) {
            navigate(result.href, { state: { participant: result } })
            return
        }

        if (result.type === 'document') {
            const projectId = result.documentDetails?.projectId

            if (projectId == null) return

            try {
                await openProjectDocument(String(projectId), {
                    id: result.id,
                    name: result.title,
                }, { downloadOnFirstOpen: true })
            } catch (error) {
                console.error('Failed to download document from search:', error)
            }
            return
        }

        setNavigationError(null)

        try {
            await navigateToSearchResult({
                result,
                currentWorkspaceId: workspace?.id,
                workspaces,
                loadProject: (projectId) => ProjectService.get(String(projectId)),
                verifyAccess: result.type === 'task'
                    ? () => TaskService.get(result.id)
                    : undefined,
                setWorkspace,
                reloadWorkspaces,
                navigate,
            })
        } catch (error) {
            console.error('Failed to open global search result:', error)
            setNavigationError(error instanceof SearchResultNavigationError
                && error.code === 'workspace-unavailable'
                ? 'Нет доступа к пространству или задаче. Возможно, доступ был отозван.'
                : 'Не удалось открыть задачу. Попробуйте ещё раз.')
        }
    }, [navigate, reloadWorkspaces, setWorkspace, workspace?.id, workspaces])

    const adapter = useMemo<GlobalSearchAdapter>(() => ({
        moduleLabel: 'COR TASK',
        search: (request) => SearchService.search({
            ...request,
            workspaceId: workspace?.id,
            currentUserIds,
            currentUserEmails,
        }),
        openResult: openSearchResult,
        getResultActions: (result) => {
            if (result.type === 'project') {
                return [
                {
                    id: 'rename',
                    label: 'Переименовать',
                    icon: <EditIcon />,
                    onSelect: () => handleProjectAction('rename', result),
                },
                {
                    id: 'duplicate',
                    label: 'Дублировать',
                    icon: <ProjectDuplicateIcon />,
                    onSelect: () => handleProjectAction('duplicate', result),
                },
                {
                    id: 'copy-link',
                    label: 'Копировать ссылку',
                    icon: <CopyLinkIcon />,
                    onSelect: () => handleProjectAction('copy-link', result),
                },
                {
                    id: 'privacy',
                    label: 'Настройки приватности',
                    icon: <PrivacySettingsIcon />,
                    onSelect: () => handleProjectAction('privacy', result),
                },
                {
                    id: 'statuses',
                    label: 'Настройка статусов',
                    icon: <ProjectStatusesIcon />,
                    onSelect: () => handleProjectAction('statuses', result),
                },
                {
                    id: 'archive',
                    label: 'Архивировать',
                    icon: <ArchiveIcon />,
                    onSelect: () => handleProjectAction('archive', result),
                },
                {
                    id: 'delete',
                    label: 'Удалить',
                    icon: <TrashIcon />,
                    danger: true,
                    onSelect: () => handleProjectAction('delete', result),
                },
                ]
            }

            if (result.type === 'task') {
                return [
                    {
                        id: 'rename',
                        label: 'Переименовать',
                        icon: <EditIcon />,
                        onSelect: () => handleTaskAction('rename', result),
                    },
                    {
                        id: 'duplicate',
                        label: 'Дублировать',
                        icon: <DuplicateIcon />,
                        onSelect: () => handleTaskAction('duplicate', result),
                    },
                    {
                        id: 'copy-link',
                        label: 'Копировать ссылку',
                        icon: <CopyLinkIcon />,
                        onSelect: () => handleTaskAction('copy-link', result),
                    },
                    {
                        id: 'move',
                        label: 'Переместить',
                        icon: <TaskMoveIcon />,
                        onSelect: () => handleTaskAction('move', result),
                    },
                    {
                        id: 'archive',
                        label: 'Архивировать',
                        icon: <ArchiveIcon />,
                        onSelect: () => handleTaskAction('archive', result),
                    },
                    {
                        id: 'delete',
                        label: 'Удалить',
                        icon: <TrashIcon />,
                        danger: true,
                        onSelect: () => handleTaskAction('delete', result),
                    },
                ]
            }

            if (result.type === 'document') {
                return [
                    {
                        id: 'rename',
                        label: 'Переименовать',
                        icon: <EditIcon />,
                        onSelect: () => handleDocumentAction('rename', result),
                    },
                    {
                        id: 'duplicate',
                        label: 'Дублировать',
                        icon: <DocumentDuplicateIcon />,
                        onSelect: () => handleDocumentAction('duplicate', result),
                    },
                    {
                        id: 'copy-link',
                        label: 'Копировать ссылку',
                        icon: <CopyLinkIcon />,
                        onSelect: () => handleDocumentAction('copy-link', result),
                    },
                    {
                        id: 'archive',
                        label: result.documentDetails?.isArchived ? 'Вернуть с архива' : 'Архивировать',
                        icon: <ArchiveIcon />,
                        onSelect: () => handleDocumentAction('archive', result),
                    },
                    {
                        id: 'move',
                        label: 'Переместить',
                        icon: <MoveIcon />,
                        onSelect: () => handleDocumentAction('move', result),
                    },
                    {
                        id: 'delete',
                        label: 'Удалить',
                        icon: <TrashIcon />,
                        danger: true,
                        onSelect: () => handleDocumentAction('delete', result),
                    },
                ]
            }

            return []
        },
    }), [currentUserEmails, currentUserIds, handleDocumentAction, handleProjectAction, handleTaskAction, openSearchResult, workspace?.id])

    useRegisterGlobalSearch(adapter)

    return (
        <>
            {navigationError && (
                <div className={styles.navigationError} role="alert">
                    <span>{navigationError}</span>
                    <button
                        type="button"
                        aria-label="Закрити повідомлення"
                        onClick={() => setNavigationError(null)}
                    >
                        ×
                    </button>
                </div>
            )}
            <ProjectRenameModal
                isOpen={Boolean(renameProject)}
                defaultName={renameProject?.name || ''}
                onClose={() => setRenameProject(null)}
                onConfirm={async (name) => {
                    if (!renameProject) return
                    await ProjectService.update(String(renameProject.id), { name })
                    setRenameProject(null)
                }}
            />
            <ProjectDuplicateModal
                isOpen={Boolean(duplicateProject)}
                defaultName={duplicateProject?.name || ''}
                defaultColor={duplicateProject?.color}
                onClose={() => setDuplicateProject(null)}
                onConfirm={async ({ name, color, copyContent }) => {
                    if (!duplicateProject) return
                    await ProjectService.duplicateProject(duplicateProject, name, undefined, { color, copyContent })
                    setDuplicateProject(null)
                }}
            />
            <ShareProjectModal
                isOpen={Boolean(privacyProject)}
                project={privacyProject}
                onClose={() => setPrivacyProject(null)}
                onSaved={({ id, is_public }) => {
                    setPrivacyProject((currentProject) => (
                        currentProject && currentProject.id === id
                            ? { ...currentProject, is_public }
                            : currentProject
                    ))
                }}
            />
            <ProjectStatusModal
                isOpen={Boolean(statusProject)}
                projectId={statusProject?.id}
                statuses={statusModalStatuses}
                onClose={() => {
                    setStatusProject(null)
                    setStatusModalStatuses([])
                }}
            />
            <ProjectDeleteModal
                isOpen={Boolean(deleteProject)}
                text={`Вы уверены, что хотите удалить проект «${deleteProject?.name || ''}»? Все задачи будут удалены.`}
                onClose={() => setDeleteProject(null)}
                onConfirm={async () => {
                    if (!deleteProject) return
                    await ProjectService.delete(String(deleteProject.id))
                    setDeleteProject(null)
                }}
            />
            <ProjectRenameModal
                isOpen={Boolean(taskToRename)}
                title="Переименовать задачу"
                defaultName={taskToRename?.task.title || ''}
                onClose={() => setTaskToRename(null)}
                onConfirm={async (title) => {
                    if (!taskToRename) return
                    await TaskService.update(
                        Number(taskToRename.task.id),
                        buildTaskUpdatePayload(taskToRename.task, Number(taskToRename.project.id), { title })
                    )
                    setTaskToRename(null)
                }}
            />
            <TaskMoveModal
                isOpen={Boolean(taskToMove)}
                currentProjectId={taskToMove?.project.id || 0}
                onClose={() => setTaskToMove(null)}
                onConfirm={async (targetProject) => {
                    if (!taskToMove) return
                    await moveTaskToProject(
                        taskToMove.task,
                        Number(taskToMove.project.id),
                        targetProject
                    )
                    setTaskToMove(null)
                }}
            />
            <ProjectDeleteModal
                isOpen={Boolean(taskToDelete)}
                title="Удалить задачу?"
                text={`Вы уверены, что хотите удалить задачу «${taskToDelete?.task.title || 'Без названия'}»? Это действие нельзя отменить.`}
                onClose={() => setTaskToDelete(null)}
                onConfirm={async () => {
                    if (!taskToDelete) return
                    await TaskService.delete(Number(taskToDelete.task.id))
                    setTaskToDelete(null)
                }}
            />
            <ProjectRenameModal
                isOpen={Boolean(documentToRename)}
                title="Переименовать документ"
                defaultName={getDocumentBaseName(documentToRename?.result.title || '')}
                onClose={() => setDocumentToRename(null)}
                onConfirm={async (name) => {
                    if (!documentToRename) return
                    await DocumentService.rename(
                        documentToRename.projectId,
                        documentToRename.result.id,
                        name
                    )
                    setDocumentToRename(null)
                }}
            />
            <TaskMoveModal
                isOpen={Boolean(documentToMove)}
                title="Переместить документ"
                currentProjectId={documentToMove?.projectId || 0}
                onClose={() => setDocumentToMove(null)}
                onConfirm={async (targetProject) => {
                    if (!documentToMove) return
                    await DocumentService.move(
                        documentToMove.projectId,
                        documentToMove.result.id,
                        {
                            project_id: targetProject.id,
                            target_project_id: targetProject.id,
                        }
                    )
                    setDocumentToMove(null)
                }}
            />
            <ProjectDeleteModal
                isOpen={Boolean(documentToDelete)}
                title="Удалить документ?"
                text={`Вы уверены, что хотите удалить документ «${documentToDelete?.result.title || 'Без названия'}»? Это действие нельзя отменить.`}
                onClose={() => setDocumentToDelete(null)}
                onConfirm={async () => {
                    if (!documentToDelete) return
                    await DocumentService.delete(
                        documentToDelete.projectId,
                        documentToDelete.result.id
                    )
                    setDocumentToDelete(null)
                }}
            />
        </>
    )
}
